/**
 * Customers.js
 * ============
 * Customer list with expandable row panel for:
 *   - Editing customer details (name, CL number, location, status)
 *   - Assigning a bundle + licence start date
 *   - Adding / removing top-ups
 *   - Adding new customers
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getCustomers, getBundles, getActiveAllocations, getAllocations,
         assignBundle, addTopup, closeAllocation,
         createCustomer, updateCustomer } from '../api';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtNum  = n => n == null ? 'Unlimited' : Number(n).toLocaleString();
const fmtGb   = g => g == null ? 'Unlimited' : `${Number(g).toLocaleString()} GB`;
const today   = () => new Date().toISOString().slice(0, 10);
const inp     = (extra = {}) => ({
  fontSize: 12, padding: '6px 9px', border: '1px solid #e5e5e5',
  borderRadius: 6, background: '#fff', outline: 'none', width: '100%', ...extra,
});

// ── Expanded panel ────────────────────────────────────────────────────────────

function ExpandedPanel({ customer, bundles, onClose, onCustomerUpdated }) {
  const [alloc,         setAlloc]         = useState(null);
  const [loadingAlloc,  setLoadingAlloc]  = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [flash,         setFlash]         = useState('');
  const [error,         setError]         = useState('');

  // Detail edit
  const [editDetails,  setEditDetails]  = useState(false);
  const [detailForm,   setDetailForm]   = useState({
    cust_name: customer.cust_name, cl_number: customer.cl_number || '',
    location: customer.location || '', is_active: customer.is_active,
  });

  // Bundle
  const [selectedBundle,    setSelectedBundle]    = useState('');
  const [bundleValidFrom,   setBundleValidFrom]   = useState(today());
  const [licenceStartDate,  setLicenceStartDate]  = useState('');
  const [contractLength,    setContractLength]    = useState(1);

  // Bundle history
  const [showHistory,     setShowHistory]     = useState(false);
  const [history,         setHistory]         = useState([]);
  const [loadingHistory,  setLoadingHistory]  = useState(false);

  // Top-up form
  const [showTopupForm,   setShowTopupForm]   = useState(false);
  const [topupForm,       setTopupForm]       = useState({
    label: '', test_limit: '', storage_limit_gb: '', valid_from: today(),
  });

  const doFlash = msg => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const loadAlloc = useCallback(() => {
    setLoadingAlloc(true);
    getActiveAllocations(customer.cust_code)
      .then(r => {
        setAlloc(r.data);
        setSelectedBundle(r.data.bundle?.bundle_id || '');
        setLicenceStartDate(r.data.licence_start_date || '');
        setContractLength(r.data.bundle?.contract_length_years || 1);
      })
      .catch(() => setAlloc(null))
      .finally(() => setLoadingAlloc(false));
  }, [customer.cust_code]);

  useEffect(() => { loadAlloc(); }, [loadAlloc]);

  // ── Save customer details ──
  async function handleSaveDetails() {
    setSaving(true); setError('');
    try {
      const r = await updateCustomer(customer.cust_code, detailForm);
      onCustomerUpdated(r.data);
      setEditDetails(false);
      doFlash('Details saved');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  // ── Assign bundle ──
  async function handleAssignBundle() {
    if (!selectedBundle) return;
    setSaving(true); setError('');
    try {
      await assignBundle(customer.cust_code, {
        bundle_id:             Number(selectedBundle),
        valid_from:            bundleValidFrom || today(),
        licence_start_date:    licenceStartDate || null,
        contract_length_years: Number(contractLength),
        created_by:            'dashboard',
      });
      loadAlloc();
      doFlash('Bundle assigned');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  // ── Add top-up ──
  async function handleAddTopup() {
    if (!topupForm.label) return;
    setSaving(true); setError('');
    try {
      await addTopup(customer.cust_code, {
        label:            topupForm.label,
        test_limit:       topupForm.test_limit        ? Number(topupForm.test_limit)        : null,
        storage_limit_gb: topupForm.storage_limit_gb  ? Number(topupForm.storage_limit_gb)  : null,
        valid_from:       topupForm.valid_from || today(),
        created_by:       'dashboard',
      });
      setTopupForm({ label: '', test_limit: '', storage_limit_gb: '', valid_from: today() });
      setShowTopupForm(false);
      loadAlloc();
      doFlash('Top-up added');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  // ── Bundle history ──
  async function handleViewHistory() {
    if (showHistory) { setShowHistory(false); return; }
    setLoadingHistory(true);
    try {
      const r = await getAllocations(customer.cust_code);
      setHistory(r.data.filter(a => a.allocation_type === 'bundle'));
      setShowHistory(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoadingHistory(false); }
  }

  // ── Close top-up ──
  async function handleCloseTopup(allocId) {
    setSaving(true); setError('');
    try {
      await closeAllocation(allocId, {});
      loadAlloc();
      doFlash('Top-up removed');
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  }

  const totals   = alloc?.totals || {};
  const topups   = alloc?.topups || [];
  const bundle   = alloc?.bundle;
  const pill     = (label, color) => (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, background: `${color}18`,
      color, border: `1px solid ${color}33`, fontWeight: 500 }}>{label}</span>
  );

  return (
    <tr>
      <td colSpan={6} style={{ padding: 0, background: '#f9fafb', borderBottom: '2px solid #378ADD20' }}>
        <div style={{ padding: '20px 24px', position: 'relative' }}>

          {/* Close + flash + error */}
          <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 16,
            background: 'none', border: 'none', fontSize: 16, color: '#aaa', cursor: 'pointer' }}>✕</button>
          {flash && (
            <div style={{ position: 'absolute', top: 14, right: 44, fontSize: 11, color: '#166534',
              background: '#dcfce7', padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>
              ✓ {flash}
            </div>
          )}
          {error && (
            <div style={{ marginBottom: 12, fontSize: 12, color: '#991b1b', background: '#fef2f2',
              padding: '6px 12px', borderRadius: 6 }}>{error}</div>
          )}

          {loadingAlloc ? (
            <div style={{ padding: '20px', color: '#999', fontSize: 13 }}>Loading…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>

              {/* ── Col 1: Customer details ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>Customer details</div>
                  <button onClick={() => setEditDetails(!editDetails)}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6,
                      border: '1px solid #e5e5e5', background: '#fff', cursor: 'pointer', color: '#555' }}>
                    {editDetails ? 'Cancel' : '✎ Edit'}
                  </button>
                </div>
                {editDetails ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[['Name', 'cust_name'], ['CL number', 'cl_number'], ['Location', 'location']].map(([l, k]) => (
                      <div key={k}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>{l}</div>
                        <input style={inp()} value={detailForm[k]}
                          onChange={e => setDetailForm(f => ({ ...f, [k]: e.target.value }))} />
                      </div>
                    ))}
                    <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <input type="checkbox" checked={detailForm.is_active}
                        onChange={e => setDetailForm(f => ({ ...f, is_active: e.target.checked }))} />
                      Active
                    </label>
                    <button onClick={handleSaveDetails} disabled={saving}
                      style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: 'none',
                        background: '#1a1a1a', color: '#fff', cursor: 'pointer', marginTop: 2 }}>
                      {saving ? 'Saving…' : 'Save details'}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {[['Code', customer.cust_code], ['Name', customer.cust_name],
                      ['CL number', customer.cl_number || '—'], ['Location', customer.location || '—']].map(([l, v]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                        <span style={{ color: '#888' }}>{l}</span>
                        <span style={{ fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: '#888' }}>Status</span>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11,
                        fontWeight: 500, background: customer.is_active ? '#dcfce7' : '#f3f4f6',
                        color: customer.is_active ? '#166534' : '#6b7280' }}>
                        {customer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Col 2: Bundle ── */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Bundle &amp; licence</div>

                {/* Current bundle display */}
                {bundle && (
                  <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8,
                    padding: '10px 12px', marginBottom: 10, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {bundle.bundle_name} {pill('Active bundle', '#378ADD')}
                    </div>
                    <div style={{ color: '#666', fontSize: 11, marginBottom: 6 }}>
                      From {bundle.valid_from} {bundle.valid_to ? `→ ${bundle.valid_to}` : '(open-ended)'}
                      {bundle.contract_length_years && (
                        <span style={{ marginLeft: 8, background: '#f3f4f6', color: '#555', borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>
                          {bundle.contract_length_years}-year contract
                        </span>
                      )}
                    </div>
                    {[['Samples', fmtNum(bundle.sample_limit)],
                      ['Tests',   fmtNum(bundle.test_limit)],
                      ['Storage', fmtGb(bundle.storage_limit_gb)]].map(([l, v]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between', color: '#555', marginBottom: 2 }}>
                        <span style={{ color: '#888' }}>{l}</span><span>{v}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Assign / change bundle */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                      {bundle ? 'Change bundle' : 'Assign bundle'}
                    </div>
                    <select style={{ ...inp(), cursor: 'pointer' }}
                      value={selectedBundle} onChange={e => setSelectedBundle(e.target.value)}>
                      <option value="">— Select bundle —</option>
                      {bundles.filter(b => b.is_active).map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Valid from</div>
                    <input type="date" style={inp()} value={bundleValidFrom}
                      onChange={e => setBundleValidFrom(e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Licence year start</div>
                    <input type="date" style={inp()} value={licenceStartDate}
                      onChange={e => setLicenceStartDate(e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Contract length</div>
                    <select style={{ ...inp(), cursor: 'pointer' }}
                      value={contractLength} onChange={e => setContractLength(e.target.value)}>
                      <option value={1}>1 year (renewal)</option>
                      <option value={3}>3 years</option>
                      <option value={5}>5 years</option>
                    </select>
                  </div>
                  <button onClick={handleAssignBundle} disabled={!selectedBundle || saving}
                    style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: 'none',
                      background: selectedBundle ? '#1a1a1a' : '#e5e5e5',
                      color: selectedBundle ? '#fff' : '#aaa',
                      cursor: selectedBundle ? 'pointer' : 'not-allowed' }}>
                    {saving ? 'Saving…' : bundle ? 'Change bundle' : 'Assign bundle'}
                  </button>
                </div>

                {/* Bundle history */}
                <div style={{ marginTop: 10 }}>
                  <button onClick={handleViewHistory} disabled={loadingHistory}
                    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6,
                      border: '1px solid #e5e5e5', background: showHistory ? '#f3f4f6' : '#fff',
                      cursor: 'pointer', color: '#555' }}>
                    {loadingHistory ? 'Loading…' : showHistory ? '▲ Hide history' : '▼ Bundle history'}
                  </button>
                  {showHistory && (
                    <div style={{ marginTop: 8 }}>
                      {history.length === 0 ? (
                        <div style={{ fontSize: 11, color: '#bbb', padding: '6px 0' }}>No bundle history found.</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                          <thead>
                            <tr style={{ background: '#f9f9f8' }}>
                              <th style={{ padding: '5px 8px', textAlign: 'left', color: '#888', fontWeight: 500, borderBottom: '1px solid #e5e5e5' }}>Bundle</th>
                              <th style={{ padding: '5px 8px', textAlign: 'left', color: '#888', fontWeight: 500, borderBottom: '1px solid #e5e5e5' }}>From</th>
                              <th style={{ padding: '5px 8px', textAlign: 'left', color: '#888', fontWeight: 500, borderBottom: '1px solid #e5e5e5' }}>To</th>
                              <th style={{ padding: '5px 8px', textAlign: 'right', color: '#888', fontWeight: 500, borderBottom: '1px solid #e5e5e5' }}>Samples</th>
                              <th style={{ padding: '5px 8px', textAlign: 'right', color: '#888', fontWeight: 500, borderBottom: '1px solid #e5e5e5' }}>Tests</th>
                              <th style={{ padding: '5px 8px', textAlign: 'right', color: '#888', fontWeight: 500, borderBottom: '1px solid #e5e5e5' }}>Storage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {history.map(h => (
                              <tr key={h.id} style={{ borderBottom: '1px solid #f0f0ee' }}>
                                <td style={{ padding: '5px 8px', fontWeight: 500 }}>{h.bundle_name || h.label}</td>
                                <td style={{ padding: '5px 8px', color: '#666' }}>{h.valid_from}</td>
                                <td style={{ padding: '5px 8px', color: h.valid_to ? '#666' : '#1D9E75', fontWeight: h.valid_to ? 400 : 500 }}>
                                  {h.valid_to || 'Active'}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', color: '#555' }}>{fmtNum(h.sample_limit)}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', color: '#555' }}>{fmtNum(h.test_limit)}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', color: '#555' }}>{fmtGb(h.storage_limit_gb)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>

                {/* Combined totals */}
                {(totals.sample_limit != null || totals.test_limit != null || totals.storage_limit_gb != null) && (
                  <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0',
                    borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: '#166534', marginBottom: 6 }}>Combined limits</div>
                    {[['Samples', fmtNum(totals.sample_limit)],
                      ['Tests',   fmtNum(totals.test_limit)],
                      ['Storage', fmtGb(totals.storage_limit_gb)]].map(([l, v]) => (
                      <div key={l} style={{ display: 'flex', justifyContent: 'space-between',
                        color: '#166534', marginBottom: 3 }}>
                        <span style={{ opacity: 0.7 }}>{l}</span>
                        <span style={{ fontWeight: 600 }}>{v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Col 3: Top-ups ── */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>Top-ups</div>
                  <button onClick={() => setShowTopupForm(f => !f)}
                    style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6,
                      border: '1px solid #e5e5e5', background: showTopupForm ? '#f3f4f6' : '#fff',
                      cursor: 'pointer', color: '#555' }}>
                    {showTopupForm ? 'Cancel' : '+ Add top-up'}
                  </button>
                </div>

                {/* Top-up form */}
                {showTopupForm && (
                  <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8,
                    padding: '12px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>
                          Label <span style={{ color: '#ef4444' }}>*</span>
                        </div>
                        <input style={inp()} placeholder="e.g. Storage top-up Apr 2025"
                          value={topupForm.label}
                          onChange={e => setTopupForm(f => ({ ...f, label: e.target.value }))} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Extra tests</div>
                          <input style={inp()} type="number" placeholder="0"
                            value={topupForm.test_limit}
                            onChange={e => setTopupForm(f => ({ ...f, test_limit: e.target.value }))} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Extra storage (GB)</div>
                          <input style={inp()} type="number" placeholder="0"
                            value={topupForm.storage_limit_gb}
                            onChange={e => setTopupForm(f => ({ ...f, storage_limit_gb: e.target.value }))} />
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 3 }}>Purchase date</div>
                        <input type="date" style={inp()} value={topupForm.valid_from}
                          onChange={e => setTopupForm(f => ({ ...f, valid_from: e.target.value }))} />
                      </div>
                      <button onClick={handleAddTopup} disabled={!topupForm.label || saving}
                        style={{ fontSize: 12, padding: '6px 12px', borderRadius: 6, border: 'none',
                          background: topupForm.label ? '#1a1a1a' : '#e5e5e5',
                          color: topupForm.label ? '#fff' : '#aaa',
                          cursor: topupForm.label ? 'pointer' : 'not-allowed' }}>
                        {saving ? 'Saving…' : 'Add top-up'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Top-up list */}
                {topups.length === 0 && !showTopupForm && (
                  <div style={{ fontSize: 12, color: '#bbb', padding: '8px 0' }}>No top-ups recorded</div>
                )}
                {topups.map(t => (
                  <div key={t.id} style={{ background: '#fff', border: '1px solid #e5e5e5',
                    borderRadius: 8, padding: '10px 12px', marginBottom: 8,
                    fontSize: 12, position: 'relative' }}>
                    <button onClick={e => { e.stopPropagation(); handleCloseTopup(t.id); }}
                      style={{ position: 'absolute', top: 7, right: 8, background: 'none',
                        border: 'none', color: '#ddd', cursor: 'pointer', fontSize: 13 }}
                      title="Remove top-up">✕</button>
                    <div style={{ fontWeight: 600, marginBottom: 5, paddingRight: 16 }}>{t.label}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 5 }}>
                      {t.test_limit        && pill(`+${Number(t.test_limit).toLocaleString()} tests`,   '#1D9E75')}
                      {t.storage_limit_gb  && pill(`+${Number(t.storage_limit_gb).toLocaleString()} GB`, '#7F77DD')}
                    </div>
                    <div style={{ color: '#aaa', fontSize: 10 }}>
                      From {t.valid_from}
                      {t.valid_to ? ` → ${t.valid_to}` : ''} · Added by {t.created_by || '—'}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const EMPTY_NEW = {
  cust_code: '', cust_name: '', cl_number: '', location: '', is_active: true,
};

export default function Customers({ activeOnly }) {
  const [customers,    setCustomers]    = useState([]);
  const [bundles,      setBundles]      = useState([]);
  const [search,       setSearch]       = useState('');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [expanded,     setExpanded]     = useState(null);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [newForm,      setNewForm]      = useState(EMPTY_NEW);
  const [addError,     setAddError]     = useState('');
  const [addSaving,    setAddSaving]    = useState(false);
  const [addFlash,     setAddFlash]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getCustomers(activeOnly), getBundles()])
      .then(([c, b]) => { setCustomers(c.data); setBundles(b.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [activeOnly]);

  useEffect(() => { load(); setExpanded(null); }, [load]);

  const filtered = customers.filter(c =>
    !search ||
    c.cust_name.toLowerCase().includes(search.toLowerCase()) ||
    c.cust_code.toLowerCase().includes(search.toLowerCase()) ||
    (c.location || '').toLowerCase().includes(search.toLowerCase())
  );

  function handleCustomerUpdated(updated) {
    setCustomers(cs => cs.map(c => c.cust_code === updated.cust_code ? { ...c, ...updated } : c));
  }

  async function handleAddCustomer() {
    const code = newForm.cust_code.trim().toUpperCase();
    if (!code || !newForm.cust_name.trim()) { setAddError('Code and name are required.'); return; }
    setAddSaving(true); setAddError('');
    try {
      const r = await createCustomer({ ...newForm, cust_code: code });
      setCustomers(cs => [...cs, r.data]);
      setNewForm(EMPTY_NEW);
      setShowAddForm(false);
      setAddFlash(true);
      setExpanded(code);
      setTimeout(() => setAddFlash(false), 2500);
    } catch (e) {
      setAddError(e.response?.data?.detail || e.message);
    } finally { setAddSaving(false); }
  }

  if (loading) return <div className="loading">Loading…</div>;
  if (error)   return <div className="error">Error: {error}</div>;

  const TH = { padding: '9px 14px', textAlign: 'left', fontWeight: 500, fontSize: 12,
    color: '#666', borderBottom: '1px solid #e5e5e5', background: '#f9f9f8', whiteSpace: 'nowrap' };
  const TD = { padding: '10px 14px', borderBottom: '1px solid #f0f0ee', fontSize: 13 };

  return (
    <>
      {/* Search + Add button */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input className="search-input" style={{ marginBottom: 0, flex: 1 }}
          placeholder="Search by name, code or location..."
          value={search}
          onChange={e => { setSearch(e.target.value); setExpanded(null); }} />
        {addFlash && (
          <span style={{ fontSize: 11, color: '#166534', background: '#dcfce7',
            padding: '4px 12px', borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap' }}>
            ✓ Customer added
          </span>
        )}
        <button onClick={() => { setShowAddForm(f => !f); setAddError(''); setNewForm(EMPTY_NEW); setExpanded(null); }}
          style={{ padding: '8px 16px', fontSize: 13, borderRadius: 6, border: 'none',
            background: showAddForm ? '#f3f4f6' : '#1a1a1a',
            color: showAddForm ? '#555' : '#fff', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {showAddForm ? '✕ Cancel' : '+ Add customer'}
        </button>
      </div>

      {/* Inline add form */}
      {showAddForm && (
        <div style={{ background: '#fff', border: '1px solid #378ADD', borderRadius: 10,
          padding: '20px 24px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>New customer</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 12, alignItems: 'end' }}>
            {[['Code *', 'cust_code', 'e.g. XYZ', false],
              ['Name *', 'cust_name', 'Customer name', false],
              ['CL number', 'cl_number', 'CL-0000', false],
              ['Location', 'location', 'City, Country', false]].map(([l, k, ph]) => (
              <div key={k}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{l}</div>
                <input style={{ fontSize: 13, padding: '7px 10px', border: '1px solid #e5e5e5',
                  borderRadius: 6, background: '#fff', outline: 'none', width: '100%' }}
                  placeholder={ph}
                  value={newForm[k]}
                  onChange={e => { setNewForm(f => ({ ...f, [k]: e.target.value })); setAddError(''); }} />
              </div>
            ))}
          </div>
          {addError && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#991b1b', background: '#fef2f2',
              padding: '6px 12px', borderRadius: 6 }}>{addError}</div>
          )}
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button onClick={handleAddCustomer} disabled={addSaving}
              style={{ padding: '7px 20px', fontSize: 13, borderRadius: 6, border: 'none',
                background: '#1a1a1a', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>
              {addSaving ? 'Saving…' : 'Save customer'}
            </button>
            <button onClick={() => { setShowAddForm(false); setAddError(''); }}
              style={{ padding: '7px 14px', fontSize: 13, borderRadius: 6,
                border: '1px solid #e5e5e5', background: '#fff', cursor: 'pointer', color: '#555' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Customer table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={TH}>Code</th>
              <th style={TH}>Name</th>
              <th style={TH}>CL number</th>
              <th style={TH}>Location</th>
              <th style={TH}>Bundle</th>
              <th style={TH}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => {
              const isOpen = expanded === c.cust_code;
              const isLast = i === filtered.length - 1;
              return [
                <tr key={c.cust_code}
                  onClick={() => setExpanded(isOpen ? null : c.cust_code)}
                  style={{ borderBottom: isOpen || isLast ? 'none' : '1px solid #f0f0ee',
                    cursor: 'pointer', background: isOpen ? '#f0f7ff' : '#fff' }}
                  onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#fafaf9'; }}
                  onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = isOpen ? '#f0f7ff' : '#fff'; }}
                >
                  <td style={{ ...TD, color: '#666', fontSize: 12, fontWeight: 500,
                    borderBottom: isOpen ? 'none' : TD.borderBottom }}>{c.cust_code}</td>
                  <td style={{ ...TD, fontWeight: 500, borderBottom: isOpen ? 'none' : TD.borderBottom }}>
                    {c.cust_name}
                    <span style={{ fontSize: 10, color: isOpen ? '#378ADD' : '#bbb', marginLeft: 6 }}>
                      {isOpen ? '▲' : '▼'}
                    </span>
                  </td>
                  <td style={{ ...TD, color: '#666', borderBottom: isOpen ? 'none' : TD.borderBottom }}>
                    {c.cl_number || '—'}
                  </td>
                  <td style={{ ...TD, color: '#666', borderBottom: isOpen ? 'none' : TD.borderBottom }}>
                    {c.location || '—'}
                  </td>
                  <td style={{ ...TD, borderBottom: isOpen ? 'none' : TD.borderBottom }}>
                    <span style={{ fontSize: 11, color: '#bbb' }}>—</span>
                  </td>
                  <td style={{ ...TD, borderBottom: isOpen ? 'none' : TD.borderBottom }}>
                    <span className={`badge ${c.is_active ? 'badge-active' : 'badge-inactive'}`}>
                      {c.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>,
                isOpen && (
                  <ExpandedPanel
                    key={`${c.cust_code}-panel`}
                    customer={c}
                    bundles={bundles}
                    onClose={() => setExpanded(null)}
                    onCustomerUpdated={handleCustomerUpdated}
                  />
                ),
              ];
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
        Showing {filtered.length} of {customers.length} customers · Click a row to manage allocations
      </div>
    </>
  );
}
